import { useMemo, useCallback, useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { DcRuntimeApi } from '@api/bff/openapi/endpoints/dc-runtime-api';
import { bffApiClient } from '@api/bff/client';
import { dcRuntimeKeys } from '@api/bff/calibrationRuntime/constants';
import { useUserStore } from '@stores/userStore';
import useOnlineStatus from '@hooks/useOnlineStatus';
import { useApiToast } from '@hooks/useApiToast';
import { db } from '@db/database';
import { enqueue, processAll } from '@services/offlineSyncService';
import type { DcRuntimeViewModel } from '@api/bff/openapi/models';
import type {
  CalibrationRuntimeData,
  CustomerSignoff,
  SyncStatus
} from '@features/calibration/hooks/useCalibrationRuntime/types';
import type { EquipmentReviewData } from '../types';

type UsePrimaryCalibrationRuntimeParams = {
  jobNumber: string;
  equipmentList: EquipmentReviewData[];
};

type UsePrimaryCalibrationRuntimeResult = {
  primaryRuntime: DcRuntimeViewModel | null;
  primaryCalibrationData: CalibrationRuntimeData | null;
  isLoading: boolean;
  updateAcknowledgment: (acknowledgment: CustomerSignoff) => Promise<void>;
  isSaving: boolean;
  saveError: Error | null;
  syncStatus: SyncStatus | null;
};

const dcRuntimeApi = new DcRuntimeApi(undefined, undefined, bffApiClient);

const parseCalibrationData = (
  jsonData: string | null | undefined
): CalibrationRuntimeData | null => {
  if (!jsonData) return null;
  try {
    return JSON.parse(jsonData) as CalibrationRuntimeData;
  } catch {
    return null;
  }
};

export const usePrimaryCalibrationRuntime = ({
  jobNumber,
  equipmentList
}: UsePrimaryCalibrationRuntimeParams): UsePrimaryCalibrationRuntimeResult => {
  const queryClient = useQueryClient();
  const userName = useUserStore(state => state.userName) ?? '';
  const isOnline = useOnlineStatus();
  const { showSuccess, showError } = useApiToast();

  const [primaryRuntime, setPrimaryRuntime] =
    useState<DcRuntimeViewModel | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<Error | null>(null);
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);

  const primaryItem = useMemo(() => {
    if (equipmentList.length === 0) return null;
    return equipmentList.reduce((min, item) =>
      item.sequenceNumber < min.sequenceNumber ? item : min
    );
  }, [equipmentList]);

  useEffect(() => {
    const fetchPrimaryRuntime = async () => {
      if (!primaryItem || !jobNumber) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      try {
        if (isOnline && primaryItem.woDetailGlobalId) {
          const response =
            await dcRuntimeApi.apiDcRuntimeByWoDetailWoDetailGlobalIdGet({
              woDetailGlobalId: primaryItem.woDetailGlobalId
            });
          setPrimaryRuntime(response.data);
        } else if (primaryItem.woDetailGlobalId) {
          const cached = await db.cachedCalibrationRuntimes
            .where('woDetailGlobalId')
            .equals(primaryItem.woDetailGlobalId)
            .first();
          setPrimaryRuntime(cached ?? null);
        } else {
          setPrimaryRuntime(null);
        }
      } catch (error) {
        console.error('Failed to fetch primary calibration runtime:', error);
        setPrimaryRuntime(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPrimaryRuntime();
  }, [primaryItem, jobNumber, isOnline]);

  const primaryCalibrationData = useMemo(
    () => parseCalibrationData(primaryRuntime?.jsonData),
    [primaryRuntime]
  );

  const updateAcknowledgment = useCallback(
    async (acknowledgment: CustomerSignoff) => {
      if (!primaryRuntime || !primaryItem) {
        showError(
          'acknowledgment.submitFailed',
          new Error('No calibration runtime'),
          'customerReview'
        );
        return;
      }

      setIsSaving(true);
      setSaveError(null);

      try {
        const currentData = parseCalibrationData(primaryRuntime.jsonData) || {};
        const updatedData: CalibrationRuntimeData = {
          ...currentData,
          customerSignoff: acknowledgment
        } as CalibrationRuntimeData;

        const updatedJsonData = JSON.stringify(updatedData);

        // Update local cache first
        if (primaryRuntime.woDetailGlobalId) {
          const cached = await db.cachedCalibrationRuntimes
            .where('woDetailGlobalId')
            .equals(primaryRuntime.woDetailGlobalId)
            .first();
          if (cached) {
            await db.cachedCalibrationRuntimes.update(cached.id!, {
              ...cached,
              jsonData: updatedJsonData,
              cachedAt: new Date().toISOString()
            });
          }
        }

        setPrimaryRuntime(prev =>
          prev ? { ...prev, jsonData: updatedJsonData } : null
        );

        // Enqueue for sync — dedup by woDetailGlobalId
        await enqueue({
          entityType: 'acknowledgment',
          entityKey: primaryRuntime.woDetailGlobalId ?? primaryRuntime.id!,
          payload: {
            calibrationRuntimeId: primaryRuntime.id!,
            updateDcRuntimeDto: {
              gagesGlobalId: primaryRuntime.gagesGlobalId || '',
              moduleType: 'SCALES',
              woDetailGlobalId: primaryRuntime.woDetailGlobalId ?? null,
              lastModifiedBy: userName,
              jsonData: updatedJsonData
            }
          },
          priority: 20
        });

        setSyncStatus('pending');
        showSuccess('acknowledgment.savedLocally', 'customerReview');

        // Trigger immediate processing if online
        if (isOnline) {
          processAll().then(() => {
            if (primaryItem.woDetailGlobalId) {
              queryClient.invalidateQueries({
                queryKey: dcRuntimeKeys.byWoDetailGlobalId(
                  primaryItem.woDetailGlobalId
                )
              });
            }
            setSyncStatus('synced');
          });
        }
      } catch (error) {
        const err =
          error instanceof Error ? error : new Error('Failed to save');
        setSaveError(err);
        showError('acknowledgment.submitFailed', err, 'customerReview');
      } finally {
        setIsSaving(false);
      }
    },
    [
      primaryRuntime,
      primaryItem,
      isOnline,
      userName,
      queryClient,
      showSuccess,
      showError
    ]
  );

  return {
    primaryRuntime,
    primaryCalibrationData,
    isLoading,
    updateAcknowledgment,
    isSaving,
    saveError,
    syncStatus
  };
};
